// third-party
import { FormattedMessage } from 'react-intl';

// assets
import {
  Activity,
  Book1,
  Category,
  Code,
  Cpu,
  Data,
  DocumentText,
  Element3,
  Key,
  Monitor,
  Note,
  People,
  Profile2User,
  Setting2,
  Shield,
  ShieldSearch,
  ShieldTick,
  Task,
  UserAdd,
  Verify,
  Wallet3,
  Warning2
} from 'iconsax-react';

// types
import { NavItemType } from 'types/menu';
import { Permission } from 'utils/permissions';
import { AccountPermission } from 'types/console';

// ==============================|| MENU ITEMS ||============================== //
//
// Titles are message ids, so the menu follows the chosen language. Two groups: the developer group is
// for every signed-in admin, and the platform group only for holders of a platform role. Each platform
// entry is then filtered by its own permission.

const developer: NavItemType = {
  id: 'group-developer',
  title: <FormattedMessage id="menu.group.developer" />,
  type: 'group',
  icon: Code,
  children: [
    { id: 'apps', title: <FormattedMessage id="menu.apps" />, type: 'item', url: '/apps', icon: Element3 },
    {
      id: 'keys',
      title: <FormattedMessage id="menu.keys" />,
      type: 'item',
      url: '/keys',
      icon: Key,
      accountPermission: 'keys.manage'
    },
    // For everyone signed in: testing needs no developer account.
    { id: 'tests', title: <FormattedMessage id="menu.tests" />, type: 'item', url: '/testing', icon: Task },
    { id: 'publishing', title: <FormattedMessage id="menu.publishing" />, type: 'item', url: '/publishing', icon: DocumentText },
    {
      id: 'verification',
      title: <FormattedMessage id="menu.verification" />,
      type: 'item',
      url: '/verification',
      icon: Verify,
      accountPermission: 'account.manage'
    },
    // Open to every member (read-only without `manage` rights); a solo account's team page offers to convert instead.
    { id: 'team', title: <FormattedMessage id="menu.team" />, type: 'item', url: '/team', icon: People, accountPermission: 'read' },
    {
      id: 'account',
      title: <FormattedMessage id="menu.account" />,
      type: 'item',
      url: '/account',
      icon: Setting2,
      accountPermission: 'read'
    },
    // The public documentation: a different (landing-styled) shell, so it opens in a new tab and the console stays where it is.
    { id: 'docs', title: <FormattedMessage id="menu.docs" />, type: 'item', url: '/docs', icon: Book1, target: true }
  ]
};

const platform: NavItemType = {
  id: 'group-platform',
  title: <FormattedMessage id="menu.group.platform" />,
  type: 'group',
  icon: Shield,
  children: [
    {
      id: 'platform-overview',
      title: <FormattedMessage id="menu.overview" />,
      type: 'item',
      url: '/platform',
      icon: Activity,
      permission: 'overview.read'
    },
    {
      id: 'platform-moderation',
      title: <FormattedMessage id="menu.moderation" />,
      type: 'item',
      url: '/platform/moderation',
      icon: ShieldSearch,
      permission: 'products.review'
    },
    {
      id: 'platform-products',
      title: <FormattedMessage id="menu.products" />,
      type: 'item',
      url: '/platform/products',
      icon: Category,
      permission: 'products.read'
    },
    {
      id: 'platform-developers',
      title: <FormattedMessage id="menu.developers" />,
      type: 'item',
      url: '/platform/developers',
      icon: Profile2User,
      permission: 'developers.read'
    },
    {
      id: 'platform-desktop',
      title: <FormattedMessage id="menu.desktop" />,
      type: 'item',
      url: '/platform/desktop',
      icon: Monitor,
      permission: 'releases.client.publish'
    },
    {
      id: 'platform-segments',
      title: <FormattedMessage id="menu.segments" />,
      type: 'item',
      url: '/platform/segments',
      icon: Data,
      permission: 'segments.read'
    },
    {
      id: 'platform-news',
      title: <FormattedMessage id="menu.news" />,
      type: 'item',
      url: '/platform/news',
      icon: Note,
      permission: 'news.read'
    },
    {
      id: 'platform-models',
      title: <FormattedMessage id="menu.models" />,
      type: 'item',
      url: '/platform/models',
      icon: Cpu,
      permission: 'models.read'
    },
    {
      id: 'platform-payments',
      title: <FormattedMessage id="menu.payments" />,
      type: 'item',
      url: '/platform/payments',
      icon: Wallet3,
      permission: 'payments.read'
    },
    {
      id: 'platform-errors',
      title: <FormattedMessage id="menu.errors" />,
      type: 'item',
      url: '/platform/errors',
      icon: Warning2,
      permission: 'errors.read'
    },
    {
      id: 'platform-audit',
      title: <FormattedMessage id="menu.audit" />,
      type: 'item',
      url: '/platform/audit',
      icon: ShieldTick,
      permission: 'audit.read'
    },
    {
      id: 'platform-admins',
      title: <FormattedMessage id="menu.admins" />,
      type: 'item',
      url: '/platform/admins',
      icon: UserAdd,
      permission: 'admins.read'
    }
  ]
};

const menuItems: { items: NavItemType[] } = {
  items: [developer, platform]
};

/**
 * The tree as one admin sees it.
 *
 * Applied by `Navigation` before it works out horizontal overflow, so overflow is never counted from
 * groups that are not rendered. A group whose children are all hidden is dropped too, leaving no
 * empty heading behind.
 */
export const menuItemsFor = (
  allows: (permission: Permission) => boolean,
  isPlatform: boolean,
  accountAllows: (permission: AccountPermission) => boolean
): { items: NavItemType[] } => {
  const visible = (item: NavItemType): boolean =>
    (!item.permission || allows(item.permission)) && (!item.accountPermission || accountAllows(item.accountPermission));

  const prune = (items: NavItemType[]): NavItemType[] =>
    items
      .filter(visible)
      .map((item) => (item.children ? { ...item, children: prune(item.children) } : item))
      .filter((item) => !item.children || item.children.length > 0);

  const groups = isPlatform ? [developer, platform] : [developer];

  return { items: prune(groups) };
};

export default menuItems;
